import { useCallback } from 'react';
import { webchatApi } from '../services/api';
import { store } from '../state/store';
import { generateClientChatId } from '../state/defaults';
import type { McpWidgetRuntimeRefs } from './types';
import { ChatMessage, LlmMessage, ToolCallState } from '../types';
import { loadFileDownloads } from '../utils/fileDownloadStorage';

interface UseWidgetConversationRuntimeArgs {
  refs: McpWidgetRuntimeRefs;
  performHealthCheck(): Promise<void>;
  seedGreeting(): void;
}

export function useWidgetConversationRuntime({
  refs,
  performHealthCheck,
  seedGreeting,
}: UseWidgetConversationRuntimeArgs) {
  const handleDrop = useCallback((file: File): void => {
    store.setFileUpload({ file, uploading: true, progress: 0, error: null });
    webchatApi
      .upload(file)
      .then((result) => {
        store.addPendingAttachment({
          fileId: result.fileId || result.fileHandle,
          fileHandle: result.fileHandle || result.fileId,
          filename: result.filename || result.originalName,
          originalName: result.originalName || result.filename,
          mimeType: result.mimeType || file.type,
          size: result.size || file.size,
          storagePath: result.storagePath,
        });
        store.setFileUpload({ file: null, uploading: false, progress: 100, error: null });
        store.requestInputFocus();
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Upload failed';
        store.setFileUpload({ file: null, uploading: false, progress: 0, error: message });
      });
  }, []);

  const resetConversation = useCallback((): void => {
    store.setState({
      messages: [],
      historyMessages: [],
      conversationId: null,
      // Fresh id so this new chat doesn't share a stream key with a still-in-flight previous one.
      clientChatId: generateClientChatId(),
      boundTurnId: null,
      toolCalls: [],
      isLoading: false,
      error: null,
      rateLimitLock: null,
    });
    seedGreeting();
  }, [seedGreeting]);

  const refreshConfig = useCallback(async (): Promise<void> => {
    const healthPromise = performHealthCheck();
    refs.healthCheckPromiseRef.current = healthPromise;
    await healthPromise;

    if (!refs.mcpClientRef.current) return;

    try {
      await refs.mcpClientRef.current.listTools();
    } catch (err) {
      console.warn('[mcp-chat-widget] Failed to refresh tools:', err);
    }
  }, [performHealthCheck, refs.healthCheckPromiseRef, refs.mcpClientRef]);

  const loadConversation = useCallback((
    conversationId: string,
    messages: ChatMessage[],
    toolCalls?: ToolCallState[],
    historyMessages?: LlmMessage[],
  ): void => {
    // Restore file download cards from localStorage for non-expired downloads
    const downloads = loadFileDownloads(conversationId);
    const restored = messages.map((msg, idx) => {
      const files = downloads.get(idx);
      if (!files || files.length === 0 || msg.role !== 'assistant') return msg;
      return { ...msg, files: [...(msg.files ?? []), ...files] };
    });

    store.setState({
      messages: restored,
      historyMessages: historyMessages ?? [],
      conversationId,
      boundTurnId: null,
      toolCalls: toolCalls ?? [],
      isLoading: false,
      error: null,
      rateLimitLock: null,
    });
  }, []);

  return {
    handleDrop,
    resetConversation,
    refreshConfig,
    loadConversation,
  };
}
